import React, { useState, useEffect } from "react";
import {
  Stack,
  Text,
  Shimmer,
  ShimmerElementType,
  IconButton,
} from "@fluentui/react";
import {
  getAssets,
  AssetSummary,
  getSecrets,
  AppRegSecretSummary,
} from "@/api/dashboard";
import styles from "@/styles/HomePage.module.css";

interface MetricCardProps {
  title: string;
  value: number;
  color?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  color = "#0078d4",
}) => {
  return (
    <div className={styles.metricCard}>
      <Text variant="large" className={styles.metricCardTitle}>
        {title}
      </Text>
      <div
        className={styles.metricCardValue}
        style={{ backgroundColor: color }}
      >
        {value}
      </div>
    </div>
  );
};

const ShimmerCard: React.FC = () => {
  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        border: "1px solid #e1e5e9",
        borderRadius: "8px",
        padding: "24px",
        minHeight: "120px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Title shimmer */}
      <div style={{ marginBottom: "12px", width: "100%" }}>
        <Shimmer
          shimmerElements={[
            { type: ShimmerElementType.line, width: "80%", height: 20 },
          ]}
          width="100%"
        />
      </div>
      {/* Value shimmer */}
      <div style={{ width: "60%" }}>
        <Shimmer
          shimmerElements={[
            { type: ShimmerElementType.line, width: "100%", height: 32 },
          ]}
          width="100%"
        />
      </div>
    </div>
  );
};

const HomePage = () => {
  const [assets, setAssets] = useState<AssetSummary | null>(null);
  const [secrets, setSecrets] = useState<AppRegSecretSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      const [assetData, secretData] = await Promise.all([
        getAssets(),
        getSecrets(),
      ]);
      setAssets(assetData || null);
      setSecrets(secretData || null);
      setError(null);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Refresh when page becomes visible (when navigating back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchDashboardData();
      }
    };

    const handleFocus = () => {
      fetchDashboardData();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className="page-header">
        <h2 className="page-title">Overview</h2>
        <IconButton
          iconProps={{ iconName: "Refresh" }}
          title="Refresh Dashboard"
          ariaLabel="Refresh Dashboard"
          onClick={handleRefresh}
          disabled={isLoading}
          styles={{
            root: { width: 40, height: 40 },
            rootHovered: { backgroundColor: "#f3f2f1" },
            icon: {
              fontSize: 18,
              color: isLoading ? "#a19f9d" : "#0078d4",
            },
            rootPressed: { backgroundColor: "#e1dfdd" },
            rootDisabled: { backgroundColor: "transparent" },
          }}
        />
      </div>

      {/* First row - Metrics Cards */}
      <div className={styles.metricsGrid}>
        {isLoading ? (
          // Loading state - show shimmer cards
          <>
            <ShimmerCard />
            <ShimmerCard />
            <ShimmerCard />
            <ShimmerCard />
          </>
        ) : error ? (
          // Error state
          <div className={styles.errorState}>
            <Text variant="medium">{error}</Text>
          </div>
        ) : (
          // Success state - show actual data
          <>
            <MetricCard
              title="Active Customers"
              value={assets?.ActiveCustomers || 0}
              color="#0078d4"
            />
            <MetricCard
              title="Active Users"
              value={assets?.ActiveUsers || 0}
              color="#106ebe"
            />
            <MetricCard
              title="Active Integrations"
              value={assets?.ActiveIntegrations || 0}
              color="#2b88d8"
            />
            <MetricCard
              title="Active Applications"
              value={assets?.ActiveApplications || 0}
              color="#5ba0e6"
            />
          </>
        )}
      </div>

      {/* Secrets Section */}
      <Text variant="xLarge" className={styles.sectionHeader}>
        Secrets
      </Text>

      {/* Second row - Secrets Cards */}
      <div className={styles.metricsGrid}>
        {isLoading ? (
          // Loading state - show shimmer cards
          <>
            <ShimmerCard />
            <ShimmerCard />
            <ShimmerCard />
          </>
        ) : error ? (
          // Error state
          <div className={styles.errorState}>
            <Text variant="medium">{error}</Text>
          </div>
        ) : (
          // Success state - show actual data
          <>
            <MetricCard
              title="Expired"
              value={secrets?.AlreadyExpired || 0}
              color="#d13438"
            />
            <MetricCard
              title="Expiring this week"
              value={secrets?.ExpiringNextWeek || 0}
              color="#ff8c00"
            />
            <MetricCard
              title="Expiring this month"
              value={secrets?.ExpiringNextMonth || 0}
              color="#faa500"
            />
          </>
        )}
      </div>

      <Text variant="large" className={styles.footerText}>
        Use the navigation menu on the left to manage customers, applications,
        integrations, and system settings.
      </Text>
    </div>
  );
};

export default HomePage;
